import type { ComponentType, CSSProperties, SVGProps } from 'react';

import classNames from 'classnames';

import { computeAlignment } from '../utils/computeAlignment';

import {
  ApplePodcastsLogo,
  CastroLogo,
  OvercastLogo,
  PocketCastsLogo,
  SpotifyLogo,
  YouTubeLogo,
} from '../podcastPlatformLogos';
import * as styles from './PodcastPlatforms.css';
import type { PodcastPlatformKey, PodcastPlatformsElementProps } from './PodcastPlatforms.types';

const PLATFORM_REGISTRY: Record<PodcastPlatformKey, { Icon: ComponentType<SVGProps<SVGSVGElement>>; label: string }> = {
  apple: { Icon: ApplePodcastsLogo, label: 'Apple Podcasts' },
  spotify: { Icon: SpotifyLogo, label: 'Spotify' },
  youtube: { Icon: YouTubeLogo, label: 'YouTube' },
  pocket_casts: { Icon: PocketCastsLogo, label: 'Pocket Casts' },
  overcast: { Icon: OvercastLogo, label: 'Overcast' },
  castro: { Icon: CastroLogo, label: 'Castro' },
};

export function PodcastPlatformsElement(props: PodcastPlatformsElementProps) {
  const { element, children, attributes, className, isLive } = props;
  const {
    data,
    padding,
    gap,
    alignment,
    mobileAlignment,
    width,

    buttonBackground,
    buttonBorderRadius,
    buttonBorderWidth,
    buttonBorderColor,
    buttonBorderStyle,
    buttonPadding,
    buttonsGap,

    iconSize,

    labelFontSize,
    labelColor,
    labelFontWeight,
    labelFontFamily,

    headingText,
    headingFontFamily,
    headingFontSize,
    headingFontWeight,
    headingColor,
  } = element.attrs || {};

  const platformLinks = data?.platform_links || {};
  const visiblePlatforms = Object.entries(platformLinks)
    .filter(([key, url]) => url && key in PLATFORM_REGISTRY)
    .map(([key, url]) => {
      const { Icon, label } = PLATFORM_REGISTRY[key as PodcastPlatformKey];
      return { key, url: url as string, Icon, label };
    });

  const cssVars = {
    '--padding': padding,
    gap,
    '--width': width,
    '--justify-content': computeAlignment(alignment),
    '--mobile-justify-content': computeAlignment(mobileAlignment || alignment),
    '--button-background': buttonBackground,
    '--button-border-radius': buttonBorderRadius,
    '--button-border-width': buttonBorderWidth,
    '--button-border-color': buttonBorderColor,
    '--button-border-style': buttonBorderStyle,
    '--button-padding': buttonPadding,
    '--buttons-gap': buttonsGap,
    '--icon-size': iconSize,
    '--label-font-size': labelFontSize,
    '--label-color': labelColor,
    '--label-font-weight': labelFontWeight,
    '--label-font-family': labelFontFamily,
    '--heading-font-size': headingFontSize,
    '--heading-font-weight': headingFontWeight,
    '--heading-font-family': headingFontFamily,
    '--heading-color': headingColor,
  } as CSSProperties;

  return (
    <div className={classNames('flex flex-col', !isLive && 'pointer-events-none')} {...attributes} style={cssVars}>
      {headingText && (
        <h4
          className={styles.heading}
          style={{
            fontFamily: headingFontFamily,
          }}
        >
          {headingText}
        </h4>
      )}
      <div className={classNames(styles.platformsContainer, className)}>
        {visiblePlatforms.map((platform) => (
          <a key={platform.key} href={platform.url} target="_blank" rel="noreferrer" className={styles.platformButton}>
            <platform.Icon aria-hidden="true" className={styles.platformIcon} />
            <span className={styles.platformLabel}>{platform.label}</span>
          </a>
        ))}
        {children}
      </div>
    </div>
  );
}
